import { useTranslation } from '@/contexts/i18n-context';
import { Button } from '@/components/button';
import { Form } from '@/components/form';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { TextInput } from '@/components/inputs/text-input';
import styles from './styles.module.css';
import { MedicalHistoryItemList } from '../medical-history-item-list';
import { requiredString } from '@/util/validators';
import { Expander } from '@/components/expander';
import { FamilyDiseaseIcon } from '../icons/family-disease-icon';
import { InputLabel } from '@/components/inputs/input-label';
import { FamilyDisease } from '@/hooks/use-medical-history-state';

const familyDiseaseSchema = z.object({
  disease: requiredString(),
});

type FormValues = z.infer<typeof familyDiseaseSchema>;

interface FamilyDiseasesFormProps {
  onRemove: (id: string) => void;
  onAdd: (text: string) => void;
  familyDiseases: FamilyDisease[];
  isOpen?: boolean;
  onExpanderClick?: () => void;
}

const translationBase = 'visit.medicalHistory.familyDiseasesForm';

export function FamilyDiseasesForm({
  onRemove,
  onAdd,
  familyDiseases,
  isOpen,
  onExpanderClick,
}: FamilyDiseasesFormProps) {
  const { t } = useTranslation();

  const form = useForm<FormValues>({
    resolver: zodResolver(familyDiseaseSchema),
    mode: 'onBlur',
    reValidateMode: 'onChange',
    criteriaMode: 'firstError',
    defaultValues: {
      disease: '',
    },
  });

  const onSubmit = (data: FormValues) => {
    onAdd(data.disease);
    form.reset();
  };
  return (
    <Expander
      icon={<FamilyDiseaseIcon className={styles.icon} />}
      title={t(`${translationBase}.title`)}
      indicatorText={t(`${translationBase}.indicatorText`)}
      collapsedText={familyDiseases.map((disease) => disease.text).join(', ')}
      isOpen={isOpen}
      onClick={onExpanderClick}
    >
      <Form onSubmit={onSubmit} form={form} className={styles.form}>
        <div className={styles.formContainer}>
          <InputLabel label={t(`${translationBase}.diseaseLabel`)} htmlFor="disease-input" />
          <TextInput
            id="disease-input"
            data-testid="medical-history-family-disease-input"
            className={styles.input}
            name="disease"
            placeholder={t(`${translationBase}.diseasePlaceholder`)}
            withIconsIndicators={false}
            autoComplete="off"
          />
          <Button
            type="submit"
            className={styles.addButton}
            data-testid="medical-history-family-diseases-add"
          >
            {t(`${translationBase}.addButton`)}
          </Button>
        </div>
        <MedicalHistoryItemList items={familyDiseases} onRemove={onRemove} />
      </Form>
    </Expander>
  );
}
